


























import {
    defineComponent,
    computed,
    ref,
    watch,
    nextTick,
    onMounted,
    onUnmounted
} from "@vue/composition-api";
import { Chart, ChartItem, registerables, TimeUnit } from "chart.js";
import "chartjs-adapter-date-fns";
import useConsumptionStatistic, { ConsumptionDataPoint } from "@/store/useConsumptionStatistic";

Chart.register(...registerables);

export default defineComponent({
    props: {
        imgSrc: { type: String },
        value: { type: Boolean, default: false },
        deviceType: { type: Number, default: 0 },
        meterId: { type: String, required: true },
        subtitle: { type: String, default: "" }
    },
    setup(props, context) {
        const chartCanvas = ref<HTMLCanvasElement>();
        const toggle_chart = ref<number>(0);
        const dialog = computed(() => props.value);

        const { getConsumptionData } = useConsumptionStatistic();
        let chart: Chart | null = null;

        const getChartConfig = (value: number): { scale: TimeUnit; title: string; interval: number } => {
            switch (value) {
                case 0: return { scale: "day", title: "Daily consumption (7 Days)", interval: 7 };
                case 1: return { scale: "day", title: "Daily consumption (30 Days)", interval: 30 };
                case 2: return { scale: "week", title: "Weekly consumption (90 Days)", interval: 90 };
                case 3: return { scale: "month", title: "Monthly consumption (365 Days)", interval: 365 };
                default: return { scale: "day", title: "Consumption", interval: 7 };
            }
        };

        const setupChart = async () => {
            const ctx = chartCanvas.value as ChartItem;
            if (!ctx) return;

            const { scale, title, interval } = getChartConfig(toggle_chart.value);
            const data = await getConsumptionData(props.meterId, interval);

            chart = new Chart(ctx, {
                type: "line",
                data: {
                    datasets: [
                        {
                            label: "kWh",
                            data: data,
                            fill: true,
                            borderColor: "rgb(75, 192, 192)",
                            tension: 0.1,
                            pointRadius: 9,
                            pointHitRadius: 3,
                            pointHoverRadius: 12
                        }
                    ]
                } as any,
                options: {
                    plugins: {
                        title: {
                            display: true,
                            text: title,
                            align: "center",
                            position: "top",
                            padding: { top: 10, bottom: 10 }
                        },
                        subtitle: {
                            display: true,
                            text: props.subtitle,
                            align: "center",
                            padding: { bottom: 10 }
                        },
                        legend: {
                            display: false
                        }
                    },
                    parsing: {
                        xAxisKey: "date",
                        yAxisKey: "kilowatts"
                    },
                    scales: {
                        x: {
                            type: "time",
                            time: {
                                unit: scale,
                                displayFormats: {
                                    day: "dd-MMM",
                                    week: "dd-MMM",
                                    month: "MMM yyyy",
                                    quarter: "MMM yyyy",
                                    year: "yyyy"
                                }
                            }
                        },
                        y: {
                            title: {
                                display: true,
                                text: "kWh"
                            }
                        }
                    }
                }
            });

            (chart.canvas as HTMLCanvasElement).style.width = "auto";
        };

        const rebuildChart = async () => {
            if (chart) {
                chart.destroy();
                chart = null;
            }
            await nextTick();
            setupChart();
        };

        const close = () => {
            if (chart) {
                chart.destroy();
                chart = null;
            }
            context.emit("input", false);
        };

        onMounted(() => {
            if (props.value) setupChart();
        });

        onUnmounted(() => {
            if (chart) {
                chart.destroy();
                chart = null;
            }
        });

        watch(() => props.value, (val) => {
            if (val) rebuildChart();
        });

        watch(() => toggle_chart.value, () => {
            rebuildChart();
        });

        return {
            dialog,
            close,
            chartCanvas,
            toggle_chart
        };
    }
});
